<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useTranslation } from "i18next-vue";
import { asyncComputed } from '@vueuse/core'

import { SettingTypes } from "@/types/enums";
import type { ChangeCallbackParams } from "@/types/config";
import type { ColorPickerValue } from "@/types/ui";
import { CfgGroups } from "@/types/enums";

import { configStore } from "@/stores/config";
import { addErrorBottom } from "@/stores/messages";

import SettingsOptionButton from "@/components/settings/SettingsOptionButton.vue";
import UCToggle from "@/components/ui/UCToggle.vue";
import UCRange from '@/components/ui/UCRange.vue';
import ColorPicker from '@/components/ui/ColorPicker.vue';
import Dialog from "@/components/elements/Dialog.vue";

const { t } = useTranslation();

const autoBrightness = ref(false);
const buttonBacklight = ref(false);
const displayBrightnessValue = ref<null | number>(5);
const buttonBrightnessValue = ref<null | number>(0);
const buttonBacklightColor = ref<number[]>([255, 255, 255]);
const config = configStore();
const settingsSection = ref();
const dialogResetBacklightColor = ref();

config.$subscribe((mutation, state) => {
  setConfigValues();
});

const displayBrightnessSetting = computed(() => {
  return {
    name: "display_brightness",
    value: displayBrightnessValue.value,
    label: t('settings.display.display_brightness'),
    type: SettingTypes.PERCENT,
    group: CfgGroups.display,
    settings: {
      min: 5
    }
  }
});

const buttonBrightnessSetting = computed(() => {
  return {
    name: "button_brightness",
    value: buttonBrightnessValue.value,
    label: t('settings.display.button_brightness'),
    type: SettingTypes.PERCENT,
    group: CfgGroups.button,
  }
});

const isModelSecond = asyncComputed(async() => {
  let model = '';
  try {
    model = await config.getDeviceModel();
  } catch (e: any) {
    addErrorBottom(e);
  }
  return model?.toLowerCase() == "ucr2";
})

async function displayBrightnessChanged(params: ChangeCallbackParams) {
  displayBrightnessValue.value = params.value as number;
  onItemChange(params);
}

async function buttonBrightnessChanged(params: ChangeCallbackParams) {
  buttonBrightnessValue.value = params.value as number;
  onItemChange(params);
}

async function autoBrightnessChanged() {
  const params: ChangeCallbackParams = {
    group: CfgGroups.display,
    name: "auto_brightness",
    value: autoBrightness.value
  }
  onItemChange(params);
}

async function buttonBacklightChanged() {
  const params: ChangeCallbackParams = {
    group: CfgGroups.button,
    name: "auto_brightness",
    value: buttonBacklight.value
  }
  onItemChange(params);
}

async function buttonBacklightColorChanged(message: ColorPickerValue) {
  buttonBacklightColor.value = message.rgb;
  const color = {
    "rgb": buttonBacklightColor.value,
  }
  const params: ChangeCallbackParams = {
    group: CfgGroups.button,
    name: "static_color",
    value: color
  }
  await onItemChange(params);

  if (!isModelSecond.value && !config.$state.config?.button?.static_color?.rgb) {
    config.getAll();
  }
}

async function onItemChange(params: ChangeCallbackParams) {
  try {
    await config.update(params.group as string, params.name as string, params.value);
  } catch (e: any) {
    addErrorBottom(e, null, settingsSection.value);
  }
}

function resetButtonBacklightColor() {
  buttonBacklightColorChanged({rgb: [255, 255, 255], hsl: [0, 0, 100]} as ColorPickerValue)
}

function startResetButtonBacklightColor() {
  dialogResetBacklightColor.value && dialogResetBacklightColor.value.open();
}

function setConfigValues() {
  displayBrightnessValue.value = config.$state.config?.display?.brightness || 5;
  autoBrightness.value = config.$state.config?.display?.auto_brightness || false;
  buttonBacklight.value = config.$state.config?.button?.auto_brightness || false;
  buttonBrightnessValue.value = config.$state.config?.button?.brightness || 0;

  if (!isModelSecond.value && config.$state.config?.button?.static_color?.rgb) {
    buttonBacklightColor.value = config.$state.config?.button?.static_color?.rgb;
  }
}

onMounted(() => {
  setConfigValues();
});

</script>
<template>
  <div class="page-settings-section page-settings-section--display" ref="settingsSection">
    <h1 class="page-settings-section__title">
      {{ $t('settings.display.name') }}
    </h1>
    <div class="page-settings-section__main">
      <!-- <UCToggle
        v-model="autoBrightness"
        @change="autoBrightnessChanged"
        :label="$t('settings.display.auto_brightness')"
        :full-w="true"
        :settings="true"
        :inactiveLabel="true"
        :description="$t('settings.display.auto_brightness_description')"
      /> -->
      <UCRange 
        v-model="displayBrightnessValue"
        name="brightness"
        :key="'display-brightness-value'"
        :definition="displayBrightnessSetting"
        :change-callback="displayBrightnessChanged"
        :value="displayBrightnessValue!"
        :settings="true"
      />
      <!-- <UCToggle
        :settings="true"
        v-model="buttonBacklight"
        @change="buttonBacklightChanged"
        :label="$t('settings.display.button_backlight')"
        :full-w="true"
        :description="$t('settings.display.button_backlight_description')"
        :inactiveLabel="true"
      /> -->
      <UCRange
        v-model="buttonBrightnessValue"
        name="brightness"
        :key="'button-brightness-value'"
        :definition="buttonBrightnessSetting"
        :change-callback="buttonBrightnessChanged"
        :value="buttonBrightnessValue!"
        :settings="true"
      />
      <SettingsOptionButton
        v-if="!isModelSecond"
        :label="$t('settings.display.button_backlight_color.label')"
        :type="'button-backlight-color'"
      >
        <template #customFields>
          <button
            @click="startResetButtonBacklightColor"
            class="button button--secondary button--icon">
            <i class="fa-light fa-arrow-rotate-left"></i>
          </button>
          <ColorPicker
            :rgb="buttonBacklightColor"
            @change="buttonBacklightColorChanged"
          />
        </template>
      </SettingsOptionButton>
    </div>
    <Dialog
      ref="dialogResetBacklightColor"
      @submit="resetButtonBacklightColor"
      :title="$t('settings.display.button_backlight_color.dialog.title')"
      :text="$t('settings.display.button_backlight_color.dialog.question')"
      :submit-text="$t('ui.reset')"
      :cancel-text="$t('ui.cancel')"
    />
  </div>
</template>